import type { IFinancialCockpitEltDocument, IFinancialCockpitQbrDocument } from '../../../api/types'
import type { IPartnerDocumentSection } from '../../../lib/documentsAndLinks'
import {
  getEltDocumentDisplayName,
  getQbrDocumentDisplayName,
  parseQbrDocumentSettings,
} from '../../../lib/documentsAndLinks'
import type { TDocumentType } from '../types'

interface IDocumentsTreeRowData {
  id: string
  name: string
  partnerId: string
  partner: string
  creator: string
  offerings: string
  date: string
}
export type IDocumentsTreeRow = IDocumentsTreeRowData &
  (
    | { type: 'elt'; document: IFinancialCockpitEltDocument }
    | { type: 'qbr'; document: IFinancialCockpitQbrDocument }
  )
export interface IDocumentsTreeGroup {
  id: string
  label: string
  rows: IDocumentsTreeRow[]
}

export const buildDocumentsTreeGroups = (
  sections: IPartnerDocumentSection[],
  type: TDocumentType,
  grouping: 'partner' | 'creator',
  search: string
): IDocumentsTreeGroup[] => {
  const rows = sections.flatMap<IDocumentsTreeRow>((section) => {
    const common = (document: { EntryId: number; CreatedBy?: string }) => ({
      id: `${section.partnerId}:${type}:${document.EntryId}`,
      partnerId: section.partnerId,
      partner: section.partnerName,
      creator: document.CreatedBy?.trim() || '',
    })
    if (type === 'elt') {
      return section.eltDocuments.map((document) => ({
        ...common(document),
        name: getEltDocumentDisplayName(document),
        offerings: '',
        date: document.FormModified || document.Created || '',
        type: 'elt' as const,
        document: { ...document },
      }))
    }
    return section.qbrDocuments.map((document) => ({
      ...common(document),
      name: getQbrDocumentDisplayName(document),
      offerings: parseQbrDocumentSettings(document.Settings).selectedOfferings?.join(', ') || '',
      date: document.Modified || document.Created || '',
      type: 'qbr' as const,
      document: { ...document },
    }))
  })
  const query = search.trim().toLowerCase()
  const groups = new Map<string, IDocumentsTreeGroup>()
  rows
    .filter((row) =>
      [row.name, row.partner, row.creator, row.offerings].some((value) =>
        value.toLowerCase().includes(query)
      )
    )
    .sort((a, b) => b.date.localeCompare(a.date))
    .forEach((row) => {
      const id = grouping === 'partner' ? row.partnerId : row.creator.toLowerCase()
      const label = grouping === 'partner' ? row.partner : row.creator
      const group: IDocumentsTreeGroup = groups.get(id) ?? { id, label, rows: [] }
      if (!groups.has(id)) {
        groups.set(id, group)
      }
      group.rows.push(row)
    })
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label))
}
